import React, { useEffect, useMemo, useState } from "react";
import { filter, map, size } from "lodash";
import styled from "styled-components";
import {
  FontWeight,
  Radius,
  borderRadius,
  content,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import AutoComplete from "@ui-kit/form/autocomplete";
import CheckboxesPanel from "@radarly/components/filterbar/panels/checkboxes";
import { Choice, Status } from "@radarly/models/filters";

interface IProps {
  onSelect: (choice: Choice, status: Status) => void;
  onGlobalSelect: (status: Status, category?: string) => void;
  choices: Choice[];
  orderedChoices: Choice[];
  globalSelectors: Status[];
  placeholder?: string;
  locale: Locale;
  noOptionMessage?: string;
  allowExclusion?: boolean;
  categories: Array<{ id: string; label: string }>;
}

const Hr = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  margin: 24px 0;
`;

const Tabs = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: -12px 0 24px;
  font-weight: ${FontWeight.Bold};
`;

const Tab = styled.div<{ selected: boolean }>`
  cursor: pointer;
  color: ${form};
  border-bottom: 3px solid;
  border-color: ${(props) =>
    props.selected ? form(props, "primary") : "transparent"};
  margin: 6px 18px 0 0;
  padding: 6px 6px 3px;
  &:hover {
    background: ${(props) => transparentize(0.85, form(props, "primary"))};
    ${borderRadius("top", Radius.Small)};
    ${(props) => !props.selected && borderRadius("bottom", Radius.Small)};
  }
`;

const FilterbarPanelsMultipleLists = React.memo(
  ({
    onSelect,
    onGlobalSelect,
    choices,
    orderedChoices,
    globalSelectors,
    placeholder,
    locale,
    noOptionMessage,
    allowExclusion = false,
    categories,
  }: IProps) => {
    const [tab, setTab] = useState(size(categories) ? categories[0].id : null);

    useEffect(() => {
      if (size(categories) && tab === null) {
        setTab(categories[0].id);
      }
    }, [categories, tab]);

    const getSelectedCount = (choices: Choice[], category: string) => {
      const total = filter(
        choices,
        (choice) => choice.category === category && choice.status !== "none"
      ).length;
      return total > 0 ? `(${total})` : null;
    };
    const checkboxesChoices = useMemo(
      () => filter(choices, (choice) => choice.category === tab),
      [choices, tab]
    );

    return (
      <>
        <AutoComplete
          choices={orderedChoices}
          onSelect={onSelect}
          placeholder={placeholder}
          locale={locale}
          noOptionMessage={noOptionMessage}
          allowExclusion={allowExclusion}
        />
        <Hr />
        {categories && (
          <Tabs>
            {map(categories, (category) => (
              <Tab
                key={category.id}
                onClick={() => setTab(category.id)}
                selected={tab === category.id}
              >
                {category.label} {getSelectedCount(choices, category.id)}
              </Tab>
            ))}
          </Tabs>
        )}
        <CheckboxesPanel
          choices={checkboxesChoices}
          onStatusChange={onSelect}
          onGlobalStatusChange={onGlobalSelect}
          globalSelectors={globalSelectors}
          allowExclusion={allowExclusion}
          locale={locale}
        />
      </>
    );
  }
);

export default FilterbarPanelsMultipleLists;
